import React, { useEffect, useRef, useState } from 'react';
import { Building2, MapPin, Target, Infinity } from 'lucide-react';

const AnimatedCounter = ({ value, label, icon: Icon, index }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const current = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    if (value === '∞' || value === 'UK') {
      return;
    }

    const target = parseInt(value);
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  const displayValue = value === '∞' || value === 'UK' ? value : count;

  return (
    <div
      ref={ref}
      className={`group bg-white/70 backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200/50 hover:border-slate-300 hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-900 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
      <div className={`text-3xl sm:text-4xl font-bold text-slate-900 mb-2 ${
        value === '∞' ? 'animate-pulse' : ''
      }`}>
        {displayValue}
      </div>
      <div className="text-xs sm:text-sm text-slate-600 font-medium">{label}</div>
    </div>
  );
};

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    { icon: Target, value: '4', label: 'Active Ventures' },
    { icon: MapPin, value: 'UK', label: 'Headquartered' },
    { icon: Building2, value: '3', label: 'Core Domains' },
    { icon: Infinity, value: '∞', label: 'Long-term View' }
  ];

  useEffect(() => {
    const current = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <section id="about" ref={sectionRef} className="relative py-12 sm:py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      {/* Floating Gradient Orbs - Reduced on mobile */}
      <div className="absolute top-20 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-blue-500/18 to-purple-500/18 rounded-full blur-3xl animate-float-orb-3"></div>
      <div className="absolute top-1/3 right-1/4 w-56 h-56 sm:w-80 sm:h-80 bg-gradient-to-br from-purple-400/15 to-blue-400/15 rounded-full blur-3xl animate-float-orb-1"></div>
      <div className="hidden md:block absolute bottom-40 left-1/3 w-72 h-72 bg-gradient-to-br from-slate-500/20 to-purple-500/20 rounded-full blur-3xl animate-float-orb-4"></div>
      <div className="hidden lg:block absolute top-1/2 right-1/3 w-88 h-88 bg-gradient-to-br from-blue-400/16 to-slate-500/16 rounded-full blur-3xl animate-float-orb-2"></div>
      <div className="absolute top-20 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg className="w-full h-16 sm:h-24 md:h-32" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64 C240,32 480,32 720,64 C960,96 1200,96 1440,64 L1440,120 L0,120 Z" fill="#0f172a" fillOpacity="1"/>
          <path d="M0,80 C320,112 640,112 960,80 C1120,64 1280,64 1440,80 L1440,120 L0,120 Z" fill="#0f172a" fillOpacity="0.5"/>
        </svg>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 px-4">
            Founder-led, outcomes-focused
          </h2>
          <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed px-4">
            <p>
              OwlWorkLabs Ltd is a UK-registered company built on a simple premise: the best products come from people who understand both the problem and the path to scale.
            </p>
            <p>
              We're not chasing trends or building for acquisition. Each venture in our portfolio exists because we identified a genuine market need, validated the approach, and committed to building something that creates lasting value.
            </p>
            <p className="hidden sm:block">
              Our structure as a venture studio means we can move resources, expertise, and technology between ventures—accelerating what works and learning from what doesn't. It's an entrepreneurial approach with operational discipline.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-20">
          {stats.map((stat, index) => (
            <AnimatedCounter
              key={stat.label}
              value={stat.value}
              label={stat.label}
              icon={stat.icon}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;